import React, { useEffect, useRef, useState } from 'react';
import './AddressAutocomplete.css';

// Free Geoapify key, set in .env (locally) and Netlify environment variables (live site)
const API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;

// Rank results near the DMV first (longitude, latitude)
const DMV_BIAS = 'proximity:-77.05,38.95';

const cleanAddress = (text) => (text || '').replace(/, United States of America$/, '');

/**
 * Address box with free Geoapify suggestions as the customer types.
 * Only full addresses with a ZIP code are suggested.
 * If the key is missing or the daily limit is reached, it works as a normal text box.
 * Optional onSelect(result) receives the picked Geoapify result (includes lat and lon).
 */
function AddressAutocomplete({ id, name, value, onChange, onSelect, placeholder, required }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const closeList = () => {
    setOpen(false);
    setActive(-1);
  };

  const fetchSuggestions = (text) => {
    clearTimeout(timerRef.current);
    if (!API_KEY || text.trim().length < 3) {
      setSuggestions([]);
      closeList();
      return;
    }
    // Wait until the customer pauses typing, so we use fewer free lookups
    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      try {
        const url =
          'https://api.geoapify.com/v1/geocode/autocomplete' +
          '?text=' + encodeURIComponent(text) +
          '&filter=countrycode:us' +
          '&bias=' + DMV_BIAS +
          '&limit=6&format=json' +
          '&apiKey=' + API_KEY;
        const res = await fetch(url, { signal: abortRef.current.signal });
        if (!res.ok) throw new Error('Geoapify returned ' + res.status);
        const data = await res.json();
        const list = (data.results || []).filter((r) => r.postcode).slice(0, 5);
        setSuggestions(list);
        setOpen(list.length > 0);
        setActive(-1);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('Address suggestions unavailable:', err.message);
      }
    }, 300);
  };

  const selectSuggestion = (result) => {
    onChange(cleanAddress(result.formatted));
    if (onSelect) onSelect(result);
    setSuggestions([]);
    closeList();
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[active]);
    } else if (e.key === 'Escape') {
      closeList();
    }
  };

  const listId = `${id}-suggestions`;

  return (
    <div className="address-autocomplete">
      <input
        type="text"
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        required={required}
        pattern=".*[0-9]{5}.*"
        title="Please choose an address from the list or include the 5 digit ZIP code"
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={active >= 0 ? `${id}-option-${active}` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
          fetchSuggestions(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        onBlur={() => {
          // Cancel any pending lookup so a late answer cannot reopen the list
          clearTimeout(timerRef.current);
          if (abortRef.current) abortRef.current.abort();
          setTimeout(closeList, 150);
        }}
      />
      {open && (
        <ul id={listId} role="listbox" className="address-suggestions">
          {suggestions.map((r, i) => (
            <li
              key={r.place_id || i}
              id={`${id}-option-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'active' : ''}
              onMouseDown={(e) => { e.preventDefault(); selectSuggestion(r); }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="suggestion-icon" aria-hidden="true">📍</span>
              <span className="suggestion-text">
                <span className="suggestion-main">{r.address_line1}</span>
                <span className="suggestion-secondary">{cleanAddress(r.address_line2)}</span>
              </span>
            </li>
          ))}
          <li className="suggestions-attribution">
            <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer">Powered by Geoapify</a>
          </li>
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
